import { Component, effect, ElementRef, HostListener, inject, input, viewChild } from '@angular/core';
import { D3Helper } from '../../..';
import { axisBottom, axisLeft, extent, pointer, scaleLinear, scaleTime, select, tickStep, ticks, timeFormat, timeParse } from 'd3';
import { DOCUMENT } from '@angular/common';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';

export interface LineChartData {
  date: string;
  [key: string]: any;
}
export interface chartConfig {
  key: string;
  label: string;
  color: string;
  legend: string;
}
export interface YConfig {
  prefix?: string;
  suffix?: string;
  yTickFormat?: any;
  yStartsFromZero?: boolean;
  ticks?: number[];
}
export interface XConfig {
  tickCount: number;
  customFormatter?: (date: Date) => string;
  isHourlyView?: boolean;
}

@Component({
  selector: 'app-multi-line-chart',
  standalone: true,
  imports: [TranslocoDirective],
  templateUrl: './multi-line-chart.component.html',
  styleUrl: './multi-line-chart.component.scss'
})
export class MultiLineChartComponent {
  private readonly transloco = inject(TranslocoService);
  private chartContainer = viewChild<ElementRef<HTMLElement>>('lineChartContainer');

  public data = input.required<Array<LineChartData>>();
  public chartConfig = input.required<Array<chartConfig>>();
  public yConfig = input<YConfig>();
  public xConfig = input<XConfig>({ tickCount: 4 });
  public margin = input<{ top: number; right: number; bottom: number; left: number, height: number }>({ top: 20, right: 0, bottom: 0, left: 0, height: 182 });
  public exportBtn = input<boolean>(false);

  private width = 0;
  private height = 0;

  constructor() {
    effect(() => {
      const container = this.chartContainer();
      const data = this.data();
      const chartConfig = this.chartConfig();
      if (container?.nativeElement && data && chartConfig) {
        this.createChart(container.nativeElement, data, chartConfig);
      }
    });
  }

  @HostListener('window:resize') onResize() {
    const container = this.chartContainer();
    const data = this.data();
    const chartConfig = this.chartConfig();
    if (container?.nativeElement && data && chartConfig) {
      this.createChart(container.nativeElement, data, chartConfig);
    }
  }

  createChart(element: HTMLElement, data: Array<LineChartData>, chartConfig: Array<chartConfig>) {
    if (!element?.getBoundingClientRect) return;

    const baseMargin = this.margin();
    const boundingRect = element.getBoundingClientRect();

    const availableWidth = boundingRect.width - baseMargin.left - baseMargin.right;
    this.height = baseMargin.height - baseMargin.top - baseMargin.bottom - 10;

    select(element).selectAll('*').remove();

    const tooltip = select(element)
      .append('div')
      .attr('class', 'tooltip')
      .style('opacity', 0)
      .style('position', 'absolute')
      .style('background', '#fff')
      .style('border-radius', '8px')
      .style('padding', '4px 6px')
      .style('box-shadow', '0 4px 10px rgba(0, 0, 0, 0.08)')
      .style('border', '1px solid #e0e0e0')
      .style('pointer-events', 'none')
      .style('font-family', '"Nunito Sans", sans-serif !important')
      .style('z-index', '999');

    const svg = select(element)
      .append('svg')
      .attr('width', availableWidth + baseMargin.left + baseMargin.right)
      .attr('height', this.height + baseMargin.top + baseMargin.bottom + 40)
      .append('g')
      .attr('transform', `translate(${baseMargin.left},${baseMargin.top})`);

    const parseDate = timeParse('%Y-%m-%d');
    const parseDatetime = timeParse('%Y-%m-%dT%H:%M:%S');
    const formattedData = data.map((d) => {
      let parsedDate: Date | null;

      // Use originalTimestamp if available (more accurate), otherwise parse the date string
      if (d['originalTimestamp']) {
        parsedDate = new Date(d['originalTimestamp']);
      } else if (d.date.includes('T')) {
        parsedDate = parseDatetime(d.date);
      } else {
        parsedDate = parseDate(d.date);
      }

      return {
        ...d,
        date: parsedDate as Date,
      };
    });

    let minY = Infinity, maxY = -Infinity;
    for (const row of formattedData as any[]) {
      for (const { key } of chartConfig) {
        const v = row[key];
        if (v != null && !isNaN(v)) {
          if (v < minY) minY = v;
          if (v > maxY) maxY = v;
        }
      }
    }
    if (!isFinite(minY) || !isFinite(maxY)) { minY = 0; maxY = 1; }
    if (minY === maxY) maxY = minY + 1;

    const yConfig = this.yConfig() ?? {};
    if (yConfig.yStartsFromZero ?? true) minY = 0;

    const tickCount = yConfig.ticks?.length ?? 7;
    const step = tickStep(minY, maxY, tickCount - 1);
    const top = Math.ceil(maxY / step) * step;
    const yTicks = ticks(minY, top, tickCount);

    const y = scaleLinear()
      .domain([minY, top])
      .range([this.height, 0]);

    const tickFmt = this.yConfig()?.yTickFormat || ((d: any) => `${this.yConfig()?.prefix || ''} ${d} ${this.yConfig()?.suffix || ''}`);
    const tmpAxis = axisLeft(y).tickValues(yTicks ?? y.ticks(7)).tickSize(0).tickFormat(tickFmt as any);
    const tmp = svg.append('g').attr('class', 'yAxisTmp').call(tmpAxis);
    const maxLabelWidth = Math.ceil(
      Math.max(0, ...tmp.selectAll('text').nodes().map((n: any) => n.getBBox().width))
    );
    tmp.remove();

    const leftPad = maxLabelWidth + 10;
    const innerWidth = Math.max(0, availableWidth - leftPad);
    this.width = innerWidth;

    const plot = svg.append('g').attr('transform', `translate(${leftPad},0)`);

    const x = scaleTime()
      .domain(extent(formattedData, (d) => d.date) as [Date, Date])
      .range([0, this.width]);

    const totalTicks = this.xConfig().tickCount;

    // Use actual data dates for ticks instead of evenly spaced interpolated dates
    const sortedDates = formattedData.map(d => d.date).sort((a, b) => a.getTime() - b.getTime());
    let tickDates: Date[] = [];

    if (sortedDates.length <= totalTicks) {
      // If we have fewer data points than desired ticks, use all data points
      tickDates = [...sortedDates];
    } else {
      // If we have more data points than desired ticks, sample evenly from the actual data
      const step = Math.floor(sortedDates.length / (totalTicks - 1));
      for (let i = 0; i < totalTicks - 1; i++) {
        tickDates.push(sortedDates[i * step]);
      }
      // Always include the last data point
      tickDates.push(sortedDates[sortedDates.length - 1]);
    }

    const xConfigValue = this.xConfig();
    const xAxis = axisBottom(x)
      .tickValues(tickDates)
      .tickFormat((d) => {
        if (xConfigValue?.customFormatter) {
          return xConfigValue.customFormatter(d as Date);
        }
        return timeFormat('%d %b')(d as Date);
      });

    const xAxisGroup = plot.append('g')
      .attr('transform', `translate(0,${this.height})`)
      .call(xAxis)
      .call((g) => {
        g.selectAll('line').remove();
        g.select('.domain').remove();
        g.selectAll('text')
          .style('fill', '#9FA1A4')
          .style('font-size', '10px')
          .style('font-weight', '400')
          .style('font-family', '"Nunito Sans", sans-serif !important')
          .style('text-anchor', 'middle');
      });

    xAxisGroup.selectAll('text').filter((_, i, nodes) => i === 0).each(function () {
      const textWidth = (this as SVGTextElement).getBBox().width;
      select(this).attr('transform', `translate(${textWidth / 2},0)`);
    });
    xAxisGroup.selectAll('text').filter((_, i, nodes) => i === nodes.length - 1).each(function () {
      const textWidth = (this as SVGTextElement).getBBox().width;
      select(this).attr('transform', `translate(${-(textWidth) / 2 - 1},0)`);
    });

    plot.append('g')
      .call(
        axisLeft(y)
          .tickValues(yTicks ?? y.ticks(6))
          .tickSize(-this.width)
          .tickFormat(tickFmt as any)
          .tickPadding(10)
      )
      .call((g) => {
        g.select('.domain').remove();
        g.selectAll('line').attr('stroke', '#E0E0E0');
        g.selectAll('text')
          .attr('text-anchor', 'end')
          .style('fill', '#9FA1A4')
          .style('font-size', '10px')
          .style('font-weight', '400')
          .style('font-family', '"Nunito Sans", sans-serif !important');
      });

    chartConfig.forEach(({ key, color }) => {
      const lineGen = D3Helper.lineFunc(x, y, key);
      plot.append('path')
        .datum(formattedData)
        .attr('fill', 'none')
        .attr('stroke', color)
        .attr('stroke-width', 2)
        .attr('d', lineGen);
    });

    const hoverGroup = plot.append('g').style('display', 'none');
    const hoverLine = hoverGroup.append('line')
      .attr('y1', 0)
      .attr('y2', this.height)
      .attr('stroke', '#aaa')
      .attr('stroke-width', 1);

    const hoverDots = chartConfig.map(({ color }) =>
      hoverGroup.append('circle')
        .attr('r', 4)
        .attr('fill', color)
        .attr('stroke', '#fff')
        .attr('stroke-width', 1.5)
    );

    const bisect = (mx: number) => {
      const date = x.invert(mx);
      let i = 0;
      while (i < formattedData.length - 1 && formattedData[i + 1].date < date) i++;
      const a = formattedData[i];
      const b = formattedData[i + 1];
      const dateMs = date.getTime();
      return b && dateMs - a.date.getTime() > b.date.getTime() - dateMs ? b : a;
    };

    plot.append('rect')
      .attr('width', this.width)
      .attr('height', this.height)
      .style('fill', 'transparent')
      .on('mouseover', () => hoverGroup.style('display', null))
      .on('mouseout', () => {
        hoverGroup.style('display', 'none');
        tooltip.transition().duration(300).style('opacity', 0);
      })
      .on('mousemove', (event) => {
        const [mx] = pointer(event);
        const d: any = bisect(mx);
        const xPos = x(d.date);

        hoverLine.attr('x1', xPos).attr('x2', xPos);
        chartConfig.forEach(({ key }, i) => {
          const value = d[key];
          if (value != null && !isNaN(value)) {
            hoverDots[i].attr('cx', xPos).attr('cy', y(value)).style('display', null);
          } else {
            hoverDots[i].style('display', 'none');
          }
        });

        const tooltipWidth = (tooltip.node() as HTMLDivElement).offsetWidth;
        const containerRect = element.getBoundingClientRect();

        const tooltipLeft =
          containerRect.right - event.clientX < tooltipWidth
            ? event.offsetX - tooltipWidth - 5
            : event.offsetX + 10;

        tooltip
          .html(`
          <div style="font-weight: 600; font-size: 13px; color: #2B2B2B; margin-bottom: 1px;">
            ${d.tooltipDateTime || timeFormat('%d %b')(d.date)}
          </div>
          ${chartConfig.map(({ key, label, color }) => {
            const value = d[key];
            const displayValue = (value != null && !isNaN(value))
              ? `${this.yConfig()?.prefix || ''}${value}${this.yConfig()?.suffix || ''}`
              : this.transloco.translate('fuelShared.charts.notAvailable');
            return `
                <div style="display: flex; align-items: center; margin-bottom: 1px; font-family: 'Nunito Sans', sans-serif;">
                  <div style="width: 12px; height: 12px; background: ${color}; border-radius: 50%; margin-right: 6px;"></div>
                  <div style="color:#9FA1A4; font-size:12px; flex: 1; margin-right: 8px;">${label}</div>
                  <div style="color: #9FA1A4; font-size: 12px;">${displayValue}</div>
                </div>  
              `;
          }).join('')
            } `)
          .style('left', `${tooltipLeft}px`)
          .style('top', `${event.offsetY - 28}px`)
          .transition()
          .duration(100)
          .style('opacity', 1);
      });
  }
}